import classnames from 'classnames/bind'
import { AnimatePresence, m } from 'framer-motion'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'

import { RichTextBlock } from '~/components/Abstracts/RichText'
import { useNavBannerContext } from '~/components/Navigation/NavigationBanner'
import { BigCloseIcon } from '~/components/UI/Icons'
import RichText from '~/components/UI/RichText'
import Table, { TableProps } from '~/components/UI/Table'

import { useStyle } from '~/providers/StyleProvider'

import useBodyScrollLock from '~/hooks/useBodyScrollLock'
import { useINPTrick } from '~/hooks/useINPTrick'

import { CTA } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type ProductSizeGuideProps = {
  className?: string
  table?: TableProps
  wysiwyg?: RichTextBlock
  isSizeGuideVisible?: boolean
  onClose?: (e) => void
}

export default function ProductSizeGuide({
  className,
  table,
  wysiwyg,
  isSizeGuideVisible,
  onClose,
}: ProductSizeGuideProps) {
  const t = useTranslate()
  const [isVisible] = useNavBannerContext()
  const { onInteract, hasInteracted } = useINPTrick()

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const richTextStyle = useStyle({
    textPreset: GlobalTextPreset.RichTextEditorial,
  })

  useBodyScrollLock(isSizeGuideVisible)

  return (
    <div
      className={cx(
        css.ProductSizeGuide,
        className,

        {
          isBannerVisible: isVisible,
          isVisible: isSizeGuideVisible,
          hasInteracted,
        },
      )}>
      <AnimatePresence>
        {isSizeGuideVisible && (
          <>
            <div className={css.children}>
              <m.div
                initial={{ y: '-100%', opacity: 0 }}
                animate={{ y: '0%', opacity: 1 }}
                exit={{ y: '-100%', opacity: 0 }}
                transition={{ duration: 0.6 }}
                className={cx(css.wrapper, gridStyle)}>
                {table && (
                  <Table
                    className={css.table}
                    hasDifferentLayoutInDesktop
                    {...table}
                  />
                )}
                {wysiwyg && (
                  <RichText
                    className={cx(css.wysiwyg, richTextStyle)}
                    render={wysiwyg}
                  />
                )}
                <button
                  className={css.closeIcon}
                  onClick={(e) => onInteract(() => onClose(e))}
                  aria-label={t(CTA.CLOSE) ?? 'close'}>
                  <BigCloseIcon
                    theme={GlobalThemeColors.White}
                    className={cx(css.icon)}
                  />
                </button>
              </m.div>
            </div>
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              key="animation-bg"
              className={cx(css.background, { isOpen: isSizeGuideVisible })}
              onClick={(e) => onInteract(() => onClose(e))}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
